import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import SuperAdminRoutes from "../superadmin/routes/SuperAdminRoutes";
import EmployeeRoutes from "../employee/routes/EmployeeRoutes";
import CutomerRoutes from "../customer/routes/CutomerRoutes";

const ACCESS_TYPES = {
  SUPER_ADMIN: "Super Admin",
  GENERAL_EMPLOYEE: "General Employee",
  CUSTOMER: "Customer",
};

export default function AppRoutes() {
  console.log('[AppRoutes] Component rendered');
  const location = useLocation();
  const { logout } = useAuth();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [routeComponent, setRouteComponent] = useState(null);

  const checkAuthAndRoute = useCallback(() => {
    console.log('[AppRoutes] checkAuthAndRoute called');
    const accessToken = localStorage.getItem("accessToken");
    const userDataStr = localStorage.getItem("userData");
    
    console.log('[AppRoutes] Checking authentication:', {
      hasAccessToken: !!accessToken,
      hasUserDataStr: !!userDataStr,
      userDataStrLength: userDataStr?.length
    });
    
    // Reset redirect state first
    setShouldRedirect(false);
    
    // If no access token or user data, redirect to login
    if (!accessToken || !userDataStr) {
      console.log('[AppRoutes] No access token or user data, redirecting to login');
      setShouldRedirect(true);
      setRouteComponent(null);
      return;
    }

    try {
      console.log('[AppRoutes] Parsing userData:', userDataStr);
      const userData = JSON.parse(userDataStr);
      
      console.log('[AppRoutes] Parsed userData:', {
        hasUserData: !!userData,
        userDataKeys: userData ? Object.keys(userData) : [],
        access: userData?.access,
        employee: userData?.employee,
        employeeAccessType: userData?.employee?.accessType,
        isTenantAdmin: userData?.isTenantAdmin,
        customer: userData?.customer,
        fullUserData: userData
      });
      
      // Determine access type - check multiple sources
      let accessType = userData?.access;
      
      // If access is not set, try to determine from other fields
      if (!accessType) {
        console.log('[AppRoutes] Access field missing, determining from other fields');
        
        // Check if tenant admin (should route to SuperAdminRoutes)
        if (userData.isTenantAdmin) {
          console.log('[AppRoutes] User is tenant admin, setting access to Super Admin');
          accessType = ACCESS_TYPES.SUPER_ADMIN;
        }
        // Check employee access type
        else if (userData.employee?.accessType) {
          console.log('[AppRoutes] Using employee.accessType:', userData.employee.accessType);
          accessType = userData.employee.accessType;
        }
        // Check if customer
        else if (userData.customer) {
          console.log('[AppRoutes] User has customer record, setting access to Customer');
          accessType = ACCESS_TYPES.CUSTOMER;
        }
        // If still no access type found, logout and redirect
        else {
          console.log('[AppRoutes] Cannot determine access type, logging out and redirecting');
          logout();
          setShouldRedirect(true);
          setRouteComponent(null);
          return;
        }
      }
      
      // Route based on access type (tenant-level roles)
      let component = null;
      console.log('[AppRoutes] Routing based on access type:', accessType);
      switch (accessType) {
        case ACCESS_TYPES.SUPER_ADMIN:
          console.log('[AppRoutes] Routing to SuperAdminRoutes');
          component = <SuperAdminRoutes />;
          break;
        case ACCESS_TYPES.GENERAL_EMPLOYEE:
          console.log('[AppRoutes] Routing to EmployeeRoutes');
          component = <EmployeeRoutes />;
          break;
        case ACCESS_TYPES.CUSTOMER:
          console.log('[AppRoutes] Routing to CustomerRoutes');
          component = <CutomerRoutes />;
          break;
        default:
          console.log('[AppRoutes] Unknown access type:', accessType);
          // If no valid access type found, logout and redirect
          console.log('[AppRoutes] No valid access type found, logging out and redirecting');
          logout();
          setShouldRedirect(true);
          setRouteComponent(null);
          return;
      }
      
      console.log('[AppRoutes] Setting route component, clearing redirect flag');
      setRouteComponent(component);
      setShouldRedirect(false);
    } catch (error) {
      // If JSON parsing fails, clear storage and redirect
      console.error('[AppRoutes] Error parsing userData:', error);
      console.log('[AppRoutes] Logging out due to parse error');
      logout();
      setShouldRedirect(true);
      setRouteComponent(null);
    }
  }, [logout]);

  useEffect(() => {
    console.log('[AppRoutes] useEffect triggered, location:', location.pathname);
    checkAuthAndRoute();
  }, [checkAuthAndRoute, location.pathname]);

  // Show loading state while checking authentication
  console.log('[AppRoutes] Render decision:', {
    shouldRedirect,
    hasRouteComponent: !!routeComponent
  });
  
  if (shouldRedirect) {
    console.log('[AppRoutes] Rendering Navigate to /');
    return <Navigate to="/" replace />;
  }

  if (!routeComponent) {
    console.log('[AppRoutes] No route component yet, returning null');
    return null; // or a loading spinner
  }

  console.log('[AppRoutes] Rendering route component');
  return routeComponent;
}
